import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AdminTopNav, AdminSideNav } from '../administrationNavigation/AdminConsoleNavigation';
import GetCookie from '../../utils/GetCookie';
import Plot from 'react-plotly.js';
import * as Flags from 'country-flag-icons/react/3x2';
import { ALPHA3_TO_ALPHA2 } from '../../utils/CountryFlag';
import { useTimezone } from '../../utils/TimezoneContext';

const API_BASE_URL = '/pceapi/administration';
const PAGE_SIZE = 5;
const HISTORY_ROUTE = '/admin-transfer-history';
const EMPTY_QUERY = { page: 1, search: '' };
const ROLE_LABELS = {
  ADMIN: 'Admin',
  SUPER_USER: 'Super User',
  GLOBAL_VIEWER: 'Global Viewer',
  COUNTRY_VIEWER: 'Country Viewer',
  STANDARD_USER: 'Standard User',
  COUNTRY_HEAD: 'Country Head',
  PROJECT_MANAGER: 'Project Manager',
  DOSSIER_DEV_LEAD: 'Dossier Dev Lead',
  DOSSIER_VIEWER: 'Dossier Viewer',
};
const ASSET_GROUPS = [
  ['projects', 'project', 'projects'],
  ['data_collections', 'Data Folder', 'Data Folders'],
];
const INITIAL_MARKETS = {
  countries: [], totalProjects: null, unassignedProjects: 0, loading: true, error: '',
};
// Plotly's choropleth carries its own world geography and matches on ISO-3,
// which is exactly what countries.iso_code stores — so a real map needs no
// topojson asset and no new dependency (plotly is already used by Copilot).
// country-flag-icons keys on ISO-2, hence the ALPHA3_TO_ALPHA2 bridge; an
// unmapped market simply renders without a flag rather than breaking the row.
// Fixed buckets, not relative to the busiest market: a band means the same
// thing on every load, so the shading can be read without the legend.
// Ranges are contiguous on purpose — 5 and 11 projects have to land somewhere.
const MARKET_BANDS = [
  { min: 12, label: '12+ projects', hint: 'High coverage', color: '#6b2fa0' },
  { min: 6, label: '6-11 projects', hint: 'Medium coverage', color: '#a970d4' },
  { min: 1, label: '1-5 projects', hint: 'Light coverage', color: '#ddc6f0' },
];
// Selection reads as a white ring rather than a dark one: near-black sat
// outside the palette and looked like an error state on a pale map. The country
// keeps its band colour, so selecting it never hides its coverage.
// Selection fills the country with a vivid indigo (--color31) instead of only
// ringing it: a near-black ring looked like an error state and a white one
// vanished against the pale land. The project count stays readable in the list
// row and the tooltip, so swapping one country's band colour costs nothing.
const MARKET_SELECTED_FILL = '#6320ff';
const MARKET_EMPTY_COLOR = '#ece7f2';
const marketColor = (count) => {
  const band = MARKET_BANDS.find((entry) => (count || 0) >= entry.min);
  return band ? band.color : MARKET_EMPTY_COLOR;
};

// ── Trends panel ─────────────────────────────────────────────────────────────
// What the chart counts, in a fixed colour order. The set passed the data-viz
// palette validator for every pair, not only neighbours (lines cross), against
// the panel's own light (#fff) and dark (#3c2846) surfaces. Amber in light and
// violet in dark sit just under 3:1 there, so colour is never the only way to
// read a line: the legend carries each total, line ends carry their names
// where they don't collide, and there is a table view.
const TREND_SERIES = [
  { key: 'users', label: 'Users', light: '#6d4aff', dark: '#7758ff' },
  { key: 'projects', label: 'Projects', light: '#e0951b', dark: '#c67d00' },
  { key: 'data_folders', label: 'Data folders', light: '#2f8fdd', dark: '#2c8ddb' },
  { key: 'presets', label: 'Presets', light: '#d4537e', dark: '#cc4c77' },
];
// Weeks and months only: day by day, low daily counts turned four crossing
// splines into noise instead of a trend.
const TREND_PERIODS = [
  { value: '12w', label: '12 weeks' },
  { value: '6m', label: '6 months' },
  { value: '12m', label: '12 months' },
];
// Plotly takes colours as values, not CSS variables, so the chart chrome is
// spelled out per theme: the panel's own surface and --ad-muted ink, and a
// hairline grid one step off the surface.
const TREND_CHROME = {
  light: { surface: '#ffffff', ink: '#1a1a1c', muted: '#726b7b', grid: '#efe9f5', spike: '#c9bcd6' },
  dark: { surface: '#3c2846', ink: '#ffffff', muted: '#cabbd2', grid: 'rgba(255,255,255,0.09)', spike: 'rgba(255,255,255,0.28)' },
};
const TREND_HEIGHT = 300;
const TREND_MARGIN = { t: 10, r: 92, b: 36, l: 44 };
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "2026-09-21" read as the calendar day it names; new Date() would take it as
// UTC midnight and could land on the day before in the admin's zone.
const formatTrendBucket = (iso, unit) => {
  const [year, month, day] = String(iso).split('-').map(Number);
  if (unit === 'month') return `${MONTH_NAMES[month - 1]} ${year}`;
  const date = `${day} ${MONTH_NAMES[month - 1]} ${year}`;
  return unit === 'week' ? `Week of ${date}` : date;
};

const formatTrendTick = (iso, unit) => {
  const [year, month, day] = String(iso).split('-').map(Number);
  return unit === 'month' ? `${MONTH_NAMES[month - 1]} ${year}` : `${day} ${MONTH_NAMES[month - 1]}`;
};

const trendTotal = (data, key) => ((data && data.series && data.series[key]) || []).reduce((sum, n) => sum + n, 0);

// The chart as plain data. One y-axis: every series is a count, and a second
// scale would only invent a correlation. A gentle spline (smoothing 0.6) with
// the axis pinned at 0, so the curve can't dip into values that never
// happened; dots mark the real points. One unified tooltip lists all four
// series at the hovered bucket, and a line's name sits at its end when it
// doesn't collide with another's — the legend covers the rest.
const trendFigure = (data, isDark) => {
  const chrome = isDark ? TREND_CHROME.dark : TREND_CHROME.light;
  const unit = data.bucket;
  const x = data.buckets;
  const last = x.length - 1;
  // Daily: a dot on every point would put 120 on the chart, so only the end.
  const dense = x.length > 16;
  const showDot = (i) => !dense || i === last;
  const peak = Math.max(0, ...TREND_SERIES.flatMap((s) => data.series[s.key] || []));
  // Ticks on the buckets themselves, counted back from the latest: Plotly's
  // own date ticks fall on Sundays, a day off the Monday a week bucket starts.
  const tickStep = Math.max(1, Math.ceil(x.length / 7));
  const tickvals = x.filter((_, i) => (last - i) % tickStep === 0);
  const yMax = Math.max(4, Math.ceil(peak * 1.15));

  const traces = TREND_SERIES.map((s) => {
    const y = data.series[s.key] || [];
    const color = isDark ? s.dark : s.light;
    return {
      type: 'scatter',
      mode: 'lines+markers',
      name: s.label,
      x,
      y,
      cliponaxis: false,
      line: { shape: 'spline', smoothing: 0.6, width: 2, color },
      marker: {
        color,
        size: y.map((_, i) => (showDot(i) ? 8 : 0)),
        line: { color: chrome.surface, width: y.map((_, i) => (showDot(i) ? 2 : 0)) },
      },
      hovertemplate: `<b>%{y:,}</b>  ${s.label}<extra></extra>`,
    };
  });

  const plotHeight = TREND_HEIGHT - TREND_MARGIN.t - TREND_MARGIN.b;
  const minGap = (16 / plotHeight) * yMax;
  const placed = [];
  const annotations = [];
  TREND_SERIES
    .map((s) => ({ s, value: (data.series[s.key] || [])[last] || 0 }))
    .sort((a, b) => b.value - a.value)
    .forEach(({ s, value }) => {
      if (placed.some((y) => Math.abs(y - value) < minGap)) return;
      placed.push(value);
      annotations.push({
        x: x[last], y: value, text: s.label, xanchor: 'left', xshift: 10, showarrow: false,
        font: { size: 11, color: chrome.muted },
      });
    });

  return {
    data: traces,
    layout: {
      height: TREND_HEIGHT,
      margin: TREND_MARGIN,
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      font: { family: 'inherit', size: 11, color: chrome.muted },
      showlegend: false,
      hovermode: 'x unified',
      hoverlabel: { bgcolor: chrome.surface, bordercolor: chrome.grid, font: { color: chrome.ink, size: 12 } },
      xaxis: {
        type: 'date',
        tickmode: 'array',
        tickvals,
        ticktext: tickvals.map((iso) => formatTrendTick(iso, unit)),
        hoverformat: unit === 'month' ? '%B %Y' : unit === 'week' ? 'Week of %-d %b %Y' : '%a, %-d %b %Y',
        showgrid: false,
        zeroline: false,
        showline: true,
        linecolor: chrome.grid,
        ticks: '',
        fixedrange: true,
        showspikes: true,
        spikemode: 'across',
        spikethickness: 1,
        spikecolor: chrome.spike,
        spikedash: 'solid',
      },
      yaxis: {
        range: [0, yMax],
        fixedrange: true,
        gridcolor: chrome.grid,
        zeroline: false,
        tickformat: ',d',
        ...(yMax <= 8 ? { dtick: 1 } : { nticks: 6 }),
      },
      annotations,
    },
  };
};

// Plotly can't read CSS variables, so the chart follows <html data-theme>
// itself — the attribute ThemeToggle sets, watched the way GraphComponent does.
function useIsDarkTheme() {
  const read = () => document.documentElement.getAttribute('data-theme') === 'dark';
  const [isDark, setIsDark] = useState(read);
  useEffect(() => {
    const observer = new MutationObserver(() => setIsDark(read()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

const INITIAL_RESOURCE = {
  rows: [], total: null, overallTotal: null,
  loading: true, error: '', updatedAt: null,
};

// Small, local SVGs: no additional icon package or font dependency.
const ICONS = {
  users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /><circle cx="9" cy="7" r="4" /></>,
  shield: <><path d="M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11Z" /><path d="m9 12 2 2 4-4" /></>,
  transfer: <><path d="M4 7h16m-4-4 4 4-4 4M20 17H4m4-4-4 4 4 4" /></>,
  search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 4.5 4.5" /></>,
  arrow: <><path d="M4 12h16m-6-6 6 6-6 6" /></>,
  chevron: <><path d="m9 5 7 7-7 7" /></>,
  down: <><path d="m6 9 6 6 6-6" /></>,
  refresh: <><path d="M20 7v5h-5M4 17v-5h5" /><path d="M6.1 6.1A8 8 0 0 1 19.5 9M4.5 15a8 8 0 0 0 13.4 2.9" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></>,
  close: <><path d="m6 6 12 12M18 6 6 18" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7h.01" /></>,
  alert: <><path d="m10.3 3.9-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3.1l-8-14a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></>,
  globe: <><circle cx="12" cy="12" r="9" /><ellipse cx="12" cy="12" rx="4" ry="9" /><path d="M3 12h18" /></>,
  trend: <><path d="m3 17 6-6 4 4 8-8" /><path d="M14 7h7v7" /></>,
  lock: <><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" /></>,
};
const Icon = ({ name, className = '' }) => (
  <svg className={`ad-icon ${className}`} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false">{ICONS[name] || ICONS.info}</svg>
);
const numberLabel = (value) => value == null ? '\u2014' : Number(value).toLocaleString();
const roleLabel = (value) => ROLE_LABELS[value] || value || 'Not assigned';
const emailHandle = (email) => typeof email === 'string' && email ? email.split('@')[0] : 'Unknown user';
// The display name when the account still exists; a deleted one leaves the
// transfer row with an email but no name to join to.
const personName = (name, email) => (typeof name === 'string' && name.trim()) || emailHandle(email);
const asArray = (value) => Array.isArray(value) ? value : [];
const initials = (name, email) => {
  const parts = String(name || emailHandle(email)).trim().split(/[\s._-]+/).filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : (parts[0] || '?').slice(0, 2)).toUpperCase();
};
const typeLabel = (type) => type === 'COMPLETE' ? 'Complete' : type === 'PARTIAL' ? 'Partial' : 'Unknown';
const readTotal = (value) => {
  if ((typeof value !== 'number' && typeof value !== 'string') || value === '' ||
      !Number.isSafeInteger(Number(value)) || Number(value) < 0) {
    throw new Error('The server did not return a valid total. Please refresh.');
  }
  return Number(value);
};
const errorText = (error) => {
  const body = error && error.response && error.response.data;
  const message = body && (body.message || body.detail);
  return typeof message === 'string' ? message : (error && error.message) || 'Unable to load this section.';
};
const requireSuccess = (body) => {
  if (body && body.status === 'NotAuthorized') {
    const error = new Error('Your session has expired. Please sign in again.');
    error.unauthorized = true;
    throw error;
  }
  if (!body || body.status !== 'success') {
    throw new Error(body && typeof body.message === 'string' ? body.message : 'Unable to load this section.');
  }
  return body;
};

// Every total comes from the API, never from the five visible rows.
// A search gets a separate unfiltered total so that KPI cards remain platform-wide.
function useAdminList(endpoint, query, refreshKey, accessToken, onUnauthorized) {
  const [resource, setResource] = useState(INITIAL_RESOURCE);
  const previousToken = useRef(accessToken);
  const { page, search } = query;

  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    const tokenChanged = previousToken.current !== accessToken;
    previousToken.current = accessToken;
    setResource((previous) => ({
      ...(tokenChanged ? INITIAL_RESOURCE : previous), rows: [], loading: true, error: '',
    }));
    const load = async () => {
      try {
        const options = { headers: { Authorization: accessToken }, signal: controller.signal, timeout: 30000 };
        const requests = [axios.post(`${API_BASE_URL}${endpoint}`, {
          page, size: PAGE_SIZE, ...(search ? { search } : {}),
        }, options)];
        if (search) requests.push(axios.post(`${API_BASE_URL}${endpoint}`, { page: 1, size: 1 }, options));
        const responses = await Promise.all(requests);
        // Check auth first, even if another response contains an ordinary error.
        const denied = responses.find((response) => response.data && response.data.status === 'NotAuthorized');
        if (denied) requireSuccess(denied.data);
        const bodies = responses.map((response) => requireSuccess(response.data));
        if (!Array.isArray(bodies[0].data)) throw new Error('The server returned an invalid list. Please refresh.');
        const total = readTotal(bodies[0].total);
        const overallTotal = search ? readTotal(bodies[1].total) : total;
        if (current) setResource({
          rows: bodies[0].data, total, overallTotal, loading: false, error: '',
          updatedAt: new Date().toISOString(),
        });
      } catch (error) {
        if (!current || controller.signal.aborted) return;
        controller.abort();
        if (error.unauthorized || (error.response && error.response.status === 401)) onUnauthorized();
        setResource({ ...INITIAL_RESOURCE, loading: false, error: errorText(error) });
      }
    };
    load();
    return () => { current = false; controller.abort(); };
  }, [endpoint, page, search, refreshKey, accessToken, onUnauthorized]);
  return resource;
}

function MetricCard({ icon, label, value, description, action, onClick, loading, error }) {
  return (
    <article className="ad-metric">
      <div className="ad-metric__top">
        <span className="ad-metric__label">{label}</span>
        <span className="ad-metric__icon"><Icon name={icon} /></span>
      </div>
      <div className="ad-metric__value" aria-busy={loading}>
        {numberLabel(value)}{loading && <span className="ad-loading-dot" />}
      </div>
      <p className="ad-muted ad-metric__description">{error ? 'Currently unavailable' : description}</p>
      <button type="button" className="ad-metric__action" onClick={onClick}>
        {action}<Icon name="arrow" />
      </button>
    </article>
  );
}
function EmptyState({ icon = 'search', title, description, onRetry }) {
  return (
    <div className="ad-empty" role={onRetry ? 'alert' : undefined}>
      <span className="ad-empty__icon"><Icon name={icon} /></span>
      <strong>{title}</strong><p>{description}</p>
      {onRetry && <button type="button" className="ad-button ad-button--secondary" onClick={onRetry}>Try again</button>}
    </div>
  );
}
function LoadingRows() {
  return <div className="ad-skeletons" role="status" aria-label="Loading records">
    {Array.from({ length: PAGE_SIZE }, (_, index) => (
      <div className="ad-skeleton-row" key={index} aria-hidden="true">
        <span className="ad-skeleton ad-skeleton--avatar" />
        <span className="ad-skeleton-lines"><span className="ad-skeleton" /><span className="ad-skeleton" /></span>
        <span className="ad-skeleton ad-skeleton--badge" />
      </div>
    ))}
    <span className="ad-sr-only">Loading records</span>
  </div>;
}
function TransferDetails({ transfer, formatDate }) {
  const details = transfer.details || {};
  const groups = ASSET_GROUPS.filter(([key]) => asArray(details[key]).length > 0);
  const genbiCount = Object.values(details.genbi || {}).reduce((sum, value) => sum + (Number(value) || 0), 0);
  const scopes = details.country_scopes || {};
  const skipped = details.skipped || {};
  const skippedCount = asArray(skipped.project_ids).length + asArray(skipped.data_collection_ids).length;
  return (
    <div className="ad-transfer-detail">
      <dl className="ad-detail-list">
        <div><dt>From</dt><dd>{transfer.source_email || 'Unknown'}{transfer.source_deleted ? ' (deleted)' : ''}</dd></div>
        <div><dt>To</dt><dd>{transfer.target_email || 'Unknown'}{transfer.target_deleted ? ' (deleted)' : ''}</dd></div>
        <div><dt>Performed by</dt><dd>{transfer.performed_by_email || 'Not recorded'}</dd></div>
        <div><dt>When</dt><dd>{formatDate(transfer.performed_at)}</dd></div>
      </dl>
      {groups.map(([key, singular, plural]) => (
        <div className="ad-asset-group" key={key}>
          <strong>{asArray(details[key]).length} {asArray(details[key]).length === 1 ? singular : plural}</strong>
          <ul>{asArray(details[key]).map((item, index) => <li key={`${item.id}-${index}`}>
            {item.name || `#${item.id}`}
            {item.target_role_after && <span className="ad-muted"> &middot; {roleLabel(item.target_role_after)}</span>}
          </li>)}</ul>
        </div>
      ))}
      {genbiCount > 0 && <p className="ad-detail-note">{genbiCount} Dossier records reassigned</p>}
      {asArray(scopes.headships_removed).length > 0 && <p className="ad-detail-note">{scopes.headships_removed.length} country headships moved</p>}
      {asArray(scopes.countries_merged).length > 0 && <p className="ad-detail-note">{scopes.countries_merged.length} country affiliations added</p>}
      {skippedCount > 0 && <p className="ad-warning">{skippedCount} items skipped because the source user did not own them.</p>}
      {groups.length === 0 && genbiCount === 0 && !asArray(scopes.headships_removed).length && !asArray(scopes.countries_merged).length &&
        <p className="ad-muted ad-detail-note">No moved items are listed in this record.</p>}
    </div>
  );
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { formatDateTime, timezone } = useTimezone();
  const accessToken = GetCookie('access_token');
  const isDark = useIsDarkTheme();
  const [trendPeriod, setTrendPeriod] = useState('12w');
  const [trendView, setTrendView] = useState('chart');
  const [trends, setTrends] = useState({ data: null, loading: true, error: '' });
  const [refreshKey, setRefreshKey] = useState(0);
  const [userQuery, setUserQuery] = useState(EMPTY_QUERY);
  const [draft, setDraft] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedTransfer, setExpandedTransfer] = useState(null);
  const [countryLookup, setCountryLookup] = useState({});
  const [markets, setMarkets] = useState(INITIAL_MARKETS);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const searchRef = useRef(null);
  const directoryRef = useRef(null);
  const marketsRef = useRef(null);
  const onUnauthorized = useCallback(() => navigate('/admin-login'), [navigate]);

  const users = useAdminList('/iam-users/list', userQuery, refreshKey, accessToken, onUnauthorized);
  const transfers = useAdminList('/ownership-transfers/list', EMPTY_QUERY, refreshKey, accessToken, onUnauthorized);

  // One call feeds both the market map and the country-name lookup used by the
  // user rows. Names are display enrichment, so a failure here must never hide
  // users — the map reports its own error and the rows fall back to IDs.
  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    setCountryLookup({});
    setMarkets((previous) => ({ ...previous, loading: true, error: '' }));
    axios.get(`${API_BASE_URL}/countries/overview`, {
      headers: { Authorization: accessToken }, signal: controller.signal, timeout: 30000,
    }).then((response) => {
      const body = requireSuccess(response.data);
      const data = body.data || {};
      const countries = asArray(data.countries);
      if (!current) return;
      setCountryLookup(Object.fromEntries(countries.map((country) => [String(country.id), country.name])));
      setMarkets({
        countries,
        totalProjects: data.total_projects,
        unassignedProjects: data.unassigned_projects || 0,
        loading: false,
        error: '',
      });
    }).catch((error) => {
      if (!current || controller.signal.aborted) return;
      if (error.unauthorized || (error.response && error.response.status === 401)) onUnauthorized();
      setMarkets({ ...INITIAL_MARKETS, loading: false, error: errorText(error) });
    });
    return () => { current = false; controller.abort(); };
  }, [accessToken, refreshKey, onUnauthorized]);

  // A new period keeps the old chart up, dimmed, until the new one lands —
  // no skeleton flash, no layout jump.
  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    setTrends((previous) => ({ ...previous, loading: true, error: '' }));
    axios.post(`${API_BASE_URL}/dashboard/trends`, { period: trendPeriod, timezone: timezone || undefined }, {
      headers: { Authorization: accessToken }, signal: controller.signal, timeout: 30000,
    }).then((response) => {
      const body = requireSuccess(response.data);
      if (current) setTrends({ data: body.data || null, loading: false, error: '' });
    }).catch((error) => {
      if (!current || controller.signal.aborted) return;
      if (error.unauthorized || (error.response && error.response.status === 401)) onUnauthorized();
      setTrends({ data: null, loading: false, error: errorText(error) });
    });
    return () => { current = false; controller.abort(); };
  }, [trendPeriod, timezone, accessToken, refreshKey, onUnauthorized]);

  const formatDate = (value) => {
    if (!value) return 'Not recorded';
    try { return formatDateTime(value) || 'Not recorded'; } catch { return 'Not recorded'; }
  };
  const refresh = useCallback(() => {
    setExpandedRow(null);
    setExpandedTransfer(null);
    setRefreshKey((key) => key + 1);
  }, []);
  const openDirectory = () => {
    setExpandedRow(null);
    directoryRef.current && directoryRef.current.scrollIntoView({ block: 'nearest' });
    searchRef.current && searchRef.current.focus();
  };
  const openMarkets = () => {
    marketsRef.current && marketsRef.current.scrollIntoView({ block: 'nearest' });
  };
  const applySearch = (event) => {
    event.preventDefault();
    setUserQuery({ page: 1, search: draft.trim() });
    setExpandedRow(null);
  };
  const clearSearch = () => {
    setDraft('');
    setUserQuery({ page: 1, search: '' });
    setExpandedRow(null);
    searchRef.current && searchRef.current.focus();
  };
  const pageTo = (page) => {
    setUserQuery((previous) => ({ ...previous, page }));
    setExpandedRow(null);
  };
  const countryNames = (user) => asArray(user.country_ids).map((id) => countryLookup[String(id)] || `Country #${id}`);
  const countryText = (user) => {
    if (user.role === 'ADMIN') return 'Not required';
    if (user.role === 'GLOBAL_VIEWER') return 'All countries';
    if (!Array.isArray(user.country_ids)) return 'Not available';
    const names = countryNames(user);
    if (names.length === 0) return 'Not assigned';
    return names.length === 1 ? names[0] : `${names.length} countries`;
  };
  // Busiest first: the list answers "where is the work" before "which markets exist".
  const rankedMarkets = [...markets.countries].sort(
    (a, b) => (b.project_count || 0) - (a.project_count || 0) || a.name.localeCompare(b.name)
  );
  const activeMarket = markets.countries.find((country) => country.iso_code === selectedMarket) || null;
  const emptyMarkets = markets.countries.filter((country) => !country.project_count);
  const active = users;
  const query = userQuery;
  const pageCount = Math.max(1, Math.ceil((active.total || 0) / PAGE_SIZE));
  const isRefreshing = users.loading || transfers.loading;
  const lastUpdated = !isRefreshing && !users.error && !transfers.error
    ? [users.updatedAt, transfers.updatedAt].filter(Boolean).sort().pop() : null;
  const trendData = trends.data && Array.isArray(trends.data.buckets) && trends.data.series ? trends.data : null;
  const trendUnit = trendData ? trendData.bucket : 'week';
  const trendIsEmpty = trendData && TREND_SERIES.every((s) => trendTotal(trendData, s.key) === 0);
  const trendChart = trendData && !trendIsEmpty && trendView === 'chart' ? trendFigure(trendData, isDark) : null;
  const startRow = active.rows.length ? (query.page - 1) * PAGE_SIZE + 1 : 0;
  const endRow = active.rows.length ? startRow + active.rows.length - 1 : 0;

  return (
    <div className="main-window ad-page">
      <AdminTopNav />
      <div className="container-fluid"><div className="row">
        <AdminSideNav />
        <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window ad-main">
          <div className="admin-overview">
            <header className="ad-header">
              <div>
                <h1>Admin dashboard</h1>
                <p className="ad-subtitle">People and ownership. One clear overview.</p>
              </div>
              <div className="ad-header__actions">
                <button type="button" className="ad-button ad-button--secondary" onClick={refresh} disabled={isRefreshing}>
                  <Icon name="refresh" className={isRefreshing ? 'ad-spin' : ''} />{isRefreshing ? 'Refreshing' : 'Refresh'}
                </button>
              </div>
            </header>

            <section className="ad-metrics" aria-label="Platform totals">
              <MetricCard icon="users" label="IAM users" value={users.overallTotal}
                description="Registered platform accounts" action="Browse users" onClick={openDirectory}
                loading={users.loading} error={users.error} />
              <MetricCard icon="globe" label="Projects" value={markets.totalProjects}
                description={markets.unassignedProjects
                  ? `${numberLabel(markets.unassignedProjects)} not assigned to a market`
                  : 'Across every onboarded market'}
                action="See markets" onClick={openMarkets}
                loading={markets.loading} error={markets.error} />
              <MetricCard icon="transfer" label="Ownership transfers" value={transfers.overallTotal}
                description="Recorded hand-overs to date" action="Open transfer history" onClick={() => navigate(HISTORY_ROUTE)}
                loading={transfers.loading} error={transfers.error} />
            </section>

            <section className="ad-panel ad-trends" aria-labelledby="ad-trends-title">
              <div className="ad-panel__heading">
                <div>
                  <h2 id="ad-trends-title">Trends</h2>
                  <p>New users, projects, data folders and presets, per {trendUnit}</p>
                </div>
                <div className="ad-trends__controls">
                  <div className="ad-segmented" role="group" aria-label="Time range">
                    {TREND_PERIODS.map((option) => (
                      <button key={option.value} type="button"
                        className={`ad-segmented__option ${trendPeriod === option.value ? 'is-active' : ''}`}
                        aria-pressed={trendPeriod === option.value}
                        onClick={() => setTrendPeriod(option.value)}>
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <button type="button" className="ad-button ad-button--secondary ad-trends__view"
                    aria-pressed={trendView === 'table'}
                    onClick={() => setTrendView((view) => (view === 'chart' ? 'table' : 'chart'))}>
                    {trendView === 'chart' ? 'Show table' : 'Show chart'}
                  </button>
                  <span className="ad-panel__heading-icon"><Icon name="trend" /></span>
                </div>
              </div>
              <div className={`ad-trends__body ${trends.loading && trendData ? 'is-refreshing' : ''}`} aria-busy={trends.loading}>
                {!trendData && trends.loading ? (
                  <div className="ad-trends__placeholder" role="status">Loading trends...</div>
                ) : !trendData ? (
                  <EmptyState icon="alert" title="Trends are unavailable" description={trends.error || 'Please refresh.'} onRetry={refresh} />
                ) : trendIsEmpty ? (
                  <EmptyState icon="trend" title="Nothing was added in this period"
                    description="New users, projects, data folders and presets will show up here." />
                ) : (
                  <>
                    <ul className="ad-trends__legend" aria-label="Totals in this period">
                      {TREND_SERIES.map((s) => (
                        <li key={s.key}>
                          <span className="ad-trends__key" style={{ background: isDark ? s.dark : s.light }} aria-hidden="true" />
                          {s.label}
                          <strong>{numberLabel(trendTotal(trendData, s.key))}</strong>
                        </li>
                      ))}
                    </ul>
                    {trendChart ? (
                      <div className="ad-trends__canvas" role="img"
                        aria-label={`Line chart of new users, projects, data folders and presets per ${trendUnit}. Show table lists every value.`}>
                        <Plot
                          className="ad-trends__plot"
                          data={trendChart.data}
                          layout={trendChart.layout}
                          config={{ displayModeBar: false, responsive: true }}
                          style={{ width: '100%', height: `${TREND_HEIGHT}px` }}
                          useResizeHandler
                        />
                      </div>
                    ) : (
                      <div className="ad-trends__canvas ad-trends__table-wrap scrollable-element">
                        <table className="ad-trends__table">
                          <caption className="ad-sr-only">New users, projects, data folders and presets per {trendUnit}, newest first</caption>
                          <thead>
                            <tr>
                              <th scope="col">{trendUnit === 'month' ? 'Month' : trendUnit === 'week' ? 'Week' : 'Day'}</th>
                              {TREND_SERIES.map((s) => (
                                <th scope="col" key={s.key}>
                                  <span className="ad-trends__key" style={{ background: isDark ? s.dark : s.light }} aria-hidden="true" />
                                  {s.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {trendData.buckets.map((bucket, index) => ({ bucket, index })).reverse().map(({ bucket, index }) => (
                              <tr key={bucket}>
                                <th scope="row">{formatTrendBucket(bucket, trendUnit)}</th>
                                {TREND_SERIES.map((s) => (
                                  <td key={s.key}>{numberLabel((trendData.series[s.key] || [])[index] || 0)}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <th scope="row">Total</th>
                              {TREND_SERIES.map((s) => <td key={s.key}>{numberLabel(trendTotal(trendData, s.key))}</td>)}
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>

            <section className="ad-panel ad-markets" ref={marketsRef} aria-labelledby="ad-markets-title">
              <div className="ad-panel__heading">
                <div>
                  <h2 id="ad-markets-title">Onboarded markets</h2>
                  <p>{markets.loading ? 'Loading markets...' : markets.error ? 'Markets unavailable'
                    : `${markets.countries.length} live ${markets.countries.length === 1 ? 'market' : 'markets'}, shaded by project count`}</p>
                </div>
                <span className="ad-panel__heading-icon"><Icon name="globe" /></span>
              </div>
              <div className="ad-markets__body">
                {markets.loading ? <LoadingRows /> : markets.error ?
                  <EmptyState icon="alert" title="Markets are unavailable" description={markets.error} onRetry={refresh} /> :
                  markets.countries.length === 0 ?
                  <EmptyState icon="globe" title="No markets yet" description="Onboarded countries will appear here." /> : (
                  <>
                    <div className="ad-markets__split">
                      <div className="ad-map">
                        <Plot
                          className="ad-map__plot"
                          data={[{
                            type: 'choropleth',
                            locationmode: 'ISO-3',
                            locations: markets.countries.map((country) => country.iso_code),
                            z: markets.countries.map((country) => country.project_count || 0),
                            text: markets.countries.map((country) => country.name),
                            // Discrete bands, so a country reads as its legend
                            // bucket rather than a point on a continuous ramp.
                            colorscale: markets.countries.map((country, index) => [
                              markets.countries.length === 1 ? index : index / (markets.countries.length - 1),
                              marketColor(country.project_count),
                            ]),
                            autocolorscale: false,
                            showscale: false,
                            marker: { line: { color: '#ffffff', width: 0.6 } },
                            hovertemplate: '<b>%{text}</b><br>%{z} projects<extra></extra>',
                          },
                          // Overlay drawing only the selected country, so the
                          // outline sits above its neighbours' borders.
                          ...(activeMarket ? [{
                            type: 'choropleth',
                            locationmode: 'ISO-3',
                            locations: [activeMarket.iso_code],
                            z: [activeMarket.project_count || 0],
                            text: [activeMarket.name],
                            colorscale: [[0, MARKET_SELECTED_FILL], [1, MARKET_SELECTED_FILL]],
                            autocolorscale: false,
                            showscale: false,
                            marker: { line: { color: '#ffffff', width: 1.4 } },
                            hovertemplate: '<b>%{text}</b><br>%{z} projects<extra></extra>',
                          }] : [])]}
                          onClick={(event) => {
                            const point = event && event.points && event.points[0];
                            if (!point || !point.location) return;
                            setSelectedMarket((current) => (current === point.location ? null : point.location));
                          }}
                          layout={{
                            geo: {
                              projection: { type: 'natural earth' },
                              showframe: false,
                              showcoastlines: false,
                              showcountries: true,
                              countrycolor: '#ffffff',
                              showland: true,
                              landcolor: MARKET_EMPTY_COLOR,
                              bgcolor: 'rgba(0,0,0,0)',
                              // Whole world, not fitbounds: fitting the bbox of
                              // the onboarded markets cropped Africa, South
                              // America and Australia out of the frame entirely.
                              // Antarctica is trimmed instead, which is where
                              // most of the dead space actually was.
                              lataxis: { range: [-56, 80] },
                              lonaxis: { range: [-168, 180] },
                            },
                            margin: { t: 6, b: 6, l: 6, r: 6 },
                            paper_bgcolor: 'rgba(0,0,0,0)',
                            plot_bgcolor: 'rgba(0,0,0,0)',
                            dragmode: 'pan',
                          }}
                          // Drag to pan, scroll or the buttons to zoom, and a
                          // reset to get back to the whole world. Everything
                          // unrelated to a map is stripped from the bar.
                          config={{
                            displayModeBar: true,
                            displaylogo: false,
                            responsive: true,
                            scrollZoom: true,
                            modeBarButtonsToRemove: ['toImage', 'hoverClosestGeo', 'select2d', 'lasso2d'],
                          }}
                          style={{ width: '100%', height: '100%' }}
                          useResizeHandler
                        />
                      </div>

                      <div className="ad-markets__list">
                        <h3>Countries by project count</h3>
                        <ul className="ad-market-rows scrollable-element">
                          {rankedMarkets.map((country) => {
                            const Flag = Flags[ALPHA3_TO_ALPHA2[country.iso_code]];
                            const isSelected = selectedMarket === country.iso_code;
                            return (
                              <li key={country.id}>
                                <button type="button"
                                  className={`ad-market-rows__row ${isSelected ? 'ad-market-rows__row--selected' : ''}`}
                                  aria-pressed={isSelected}
                                  title={`${country.name}: ${numberLabel(country.project_count)} projects`}
                                  onClick={() => setSelectedMarket(isSelected ? null : country.iso_code)}>
                                  <span className="ad-market-rows__flag" aria-hidden="true">
                                    {Flag ? <Flag /> : <span className="ad-market-rows__code">{country.iso_code}</span>}
                                  </span>
                                  <span className="ad-market-rows__name">{country.name}</span>
                                  <span className="ad-market-rows__dot" style={{ background: marketColor(country.project_count) }} aria-hidden="true" />
                                  <span className="ad-market-rows__count">{numberLabel(country.project_count)}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>

                    <div className="ad-map__legend">
                      {MARKET_BANDS.map((band) => (
                        <span key={band.label} className="ad-map__legend-item">
                          <i style={{ background: band.color }} aria-hidden="true" />
                          <span>
                            <strong>{band.label}</strong>
                            <small>{band.hint}</small>
                          </span>
                        </span>
                      ))}
                      {/* The one thing the map cannot show: an onboarded market
                          nobody has put a project in yet. */}
                      {emptyMarkets.length > 0 && (
                        <span className="ad-map__legend-note">
                          <Icon name="info" />
                          <span>
                            <strong>
                              {emptyMarkets.length} {emptyMarkets.length === 1 ? 'market has' : 'markets have'} no projects yet
                            </strong>
                            <small>{emptyMarkets.map((country) => country.iso_code).join(' · ')}</small>
                          </span>
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </section>

            <div className="ad-workspace">
              <section className="ad-panel ad-directory" ref={directoryRef} aria-labelledby="ad-directory-title">
                <div className="ad-panel__heading">
                  <div><h2 id="ad-directory-title">People &amp; access</h2><p>Every platform account, newest first.</p></div>
                  <span className="ad-panel__heading-icon"><Icon name="users" /></span>
                </div>
                <div id="ad-directory-content">
                  <form className="ad-search" role="search" onSubmit={applySearch}>
                    <Icon name="search" />
                    <input ref={searchRef} value={draft} type="search" autoComplete="off"
                      aria-label="Search users by name or email"
                      placeholder="Search by name or email..."
                      onChange={(event) => setDraft(event.target.value)} />
                    {(draft || query.search) && <button type="button" className="ad-icon-button" onClick={clearSearch} aria-label="Clear search"><Icon name="close" /></button>}
                    <button type="submit" className="ad-search__submit" disabled={active.loading}>Search</button>
                  </form>
                  {query.search && <p className="ad-search-context">Results for <strong>{query.search}</strong><button type="button" onClick={clearSearch}>Clear</button></p>}
                  <div className="ad-directory__body" aria-busy={active.loading}>
                    {active.loading ? <LoadingRows /> : active.error ?
                      <EmptyState icon="alert" title="This directory is unavailable" description={active.error} onRetry={refresh} /> :
                      active.rows.length === 0 ? <EmptyState
                        icon="users"
                        title={query.search ? 'No matches found' : 'No users on this page'}
                        description={query.search ? 'Try another name or clear the search.' : 'Use Manage Users in the sidebar to add accounts.'} /> : (
                        <div className="ad-table-scroll">
                          <table className="ad-table ad-table--users">
                            <caption className="ad-sr-only">IAM users, newest first</caption>
                            <thead><tr>
                              <th scope="col">User</th>
                              <th scope="col">Platform role</th>
                              <th scope="col">Countries</th>
                            </tr></thead>
                            <tbody>{active.rows.map((item, index) => {
                              const rowId = `users-${item.user_id || item.id || index}`;
                              const detailId = `ad-row-detail-users-${index}`;
                              const isExpanded = expandedRow === rowId;
                              return <Fragment key={rowId}>
                                <tr className={isExpanded ? 'ad-table__row--open' : ''}>
                                  <td><div className="ad-person">
                                    <span className="ad-avatar" aria-hidden="true">
                                      {initials(item.name, item.email)}
                                    </span>
                                    <div className="ad-person__text">
                                      <button type="button" className="ad-person__name" title={item.name || item.email}
                                        onClick={() => setExpandedRow(isExpanded ? null : rowId)} aria-expanded={isExpanded} aria-controls={detailId}>
                                        {item.name || item.email || 'Unnamed'}
                                      </button>
                                      <span className="ad-person__secondary" title={item.email}>
                                        {item.email || 'Email not available'}
                                      </span>
                                    </div>
                                  </div></td>
                                  <td><span className={`ad-badge ${item.role === 'ADMIN' ? 'ad-badge--accent' : ''}`}>
                                    {roleLabel(item.role)}
                                  </span></td>
                                  <td><div className="ad-country" title={countryNames(item).join(', ') || countryText(item)}>
                                    <span>{countryText(item)}</span>
                                    {asArray(item.head_of_country_ids).length > 0 && <small>Country head &times;{item.head_of_country_ids.length}</small>}
                                  </div></td>
                                </tr>
                                {isExpanded && <tr id={detailId} className="ad-table__detail"><td colSpan={3}>
                                  <dl className="ad-detail-list ad-detail-list--user">
                                    <div><dt>Email</dt><dd>{item.email || 'Not available'}</dd></div>
                                    <div><dt>Country assignment</dt><dd>{item.role === 'ADMIN' ? 'Not required for Admin' : item.role === 'GLOBAL_VIEWER' ? 'All countries (from platform role)' : countryNames(item).join(', ') || countryText(item)}</dd></div>
                                    {asArray(item.head_of_country_ids).length > 0 && <div><dt>Country head</dt><dd>{item.head_of_country_ids.map((id) => countryLookup[String(id)] || `Country #${id}`).join(', ')}</dd></div>}
                                    <div><dt>Created</dt><dd>{formatDate(item.created_at)}</dd></div>
                                    <div><dt>Created by</dt><dd>{item.creator_email || 'Not recorded'}</dd></div>
                                  </dl>
                                </td></tr>}
                              </Fragment>;
                            })}</tbody>
                          </table>
                        </div>
                      )}
                  </div>
                  <div className="ad-pagination">
                    <span aria-live="polite">{active.loading ? 'Loading records...' : active.error ? 'Records unavailable' :
                      `Showing ${startRow}-${endRow} of ${numberLabel(active.total)}${query.search ? ' results' : ''}`}</span>
                    <div>
                      <button type="button" className="ad-icon-button" aria-label="Previous page" disabled={active.loading || !!active.error || query.page <= 1} onClick={() => pageTo(query.page - 1)}><Icon name="chevron" className="ad-rotate" /></button>
                      <span>{query.page} / {pageCount}</span>
                      <button type="button" className="ad-icon-button" aria-label="Next page" disabled={active.loading || !!active.error || query.page >= pageCount} onClick={() => pageTo(query.page + 1)}><Icon name="chevron" /></button>
                    </div>
                  </div>
                  <p className="ad-directory__hint">Create or edit accounts from Manage Users in the sidebar.</p>
                </div>
              </section>

              <section className="ad-panel ad-activity" aria-labelledby="ad-activity-title">
                <div className="ad-panel__heading">
                  <div><h2 id="ad-activity-title">Ownership activity</h2><p>{transfers.loading ? 'Loading recent transfers...' : transfers.error ? 'Recent transfers unavailable' :
                    `Latest ${transfers.rows.length} of ${numberLabel(transfers.total)} recorded transfers`}</p></div>
                  <span className="ad-panel__heading-icon"><Icon name="clock" /></span>
                </div>
                <div className="ad-activity__body" aria-busy={transfers.loading}>
                  {transfers.loading ? <LoadingRows /> : transfers.error ? <EmptyState icon="alert" title="Activity is unavailable" description={transfers.error} onRetry={refresh} /> :
                    transfers.rows.length === 0 ? <EmptyState icon="transfer" title="No transfers yet" description="Recorded ownership hand-overs will appear here." /> :
                    <ol className="ad-timeline">{transfers.rows.map((transfer, index) => {
                      const transferKey = transfer.id == null ? `transfer-${index}` : transfer.id;
                      const open = expandedTransfer === transferKey;
                      const summary = ASSET_GROUPS.map(([key, singular, plural]) => {
                        const count = asArray((transfer.details || {})[key]).length;
                        return count ? `${count} ${count === 1 ? singular : plural}` : null;
                      }).filter(Boolean).join(' \u00b7 ');
                      return <li key={transferKey} className={open ? 'ad-timeline__item--open' : ''}>
                        <span className="ad-timeline__marker"><Icon name="transfer" /></span>
                        <div className="ad-timeline__content">
                          <button type="button" className="ad-transfer-toggle" aria-expanded={open} aria-controls={`ad-transfer-${index}`}
                            aria-label={`${open ? 'Hide' : 'View'} transfer from ${transfer.source_email || 'unknown user'} to ${transfer.target_email || 'unknown user'}`}
                            onClick={() => setExpandedTransfer(open ? null : transferKey)}>
                            <span className="ad-transfer__people"><span title={transfer.source_email}>{personName(transfer.source_name, transfer.source_email)}</span><Icon name="arrow" /><span title={transfer.target_email}>{personName(transfer.target_name, transfer.target_email)}</span></span>
                            <span className="ad-transfer__meta"><span className={`ad-badge ad-badge--${String(transfer.transfer_type || 'unknown').toLowerCase()}`}>{typeLabel(transfer.transfer_type)}</span><Icon name="down" className={open ? 'ad-flip' : ''} /></span>
                          </button>
                          <p className="ad-transfer__date">{formatDate(transfer.performed_at)}</p>
                          {summary && <p className="ad-transfer__summary">{summary}</p>}
                          {open && <div id={`ad-transfer-${index}`}><TransferDetails transfer={transfer} formatDate={formatDate} /></div>}
                        </div>
                      </li>;
                    })}</ol>}
                </div>
                <div className="ad-activity__footer">
                  <p><Icon name="info" />Complete / Partial describes scope, not status.</p>
                  <button type="button" className="ad-button ad-button--history" onClick={() => navigate(HISTORY_ROUTE)}>View transfer history<Icon name="arrow" /></button>
                </div>
              </section>
            </div>


            <div className="ad-footer-note">
              <span aria-live="polite">{isRefreshing ? 'Updating overview...' : lastUpdated ? `Updated ${formatDate(lastUpdated)}` : 'Some sections could not be updated.'}</span>
            </div>
          </div>
        </main>
      </div></div>

    </div>
  );
};
export default AdminDashboard;
